'use client';

import { useIoTFieldData } from '@/domains/iot-v2/provider';

import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AlarmNotificationModal } from '@/components/ui/alarm-notification-modal';
import { useAlarmSoundSettings } from '@/hooks/realtime/useAlarmSoundSettings';
import { deriveAlarmItemFromMessage, type AlarmItem } from '@/domains/widgets/realtime-alarm-list-model';
import { updateAlarmCheck } from '@/shared/api/alarms-client';
import type { AlarmSoundSettings } from '@/shared/api/alarm-sound-settings-client';
import { zIndex } from '@/shared/ui/z-index';
import { debugRealtimeAlarmStage } from '@/shared/realtime/realtime-alarm-debug';
import { buildAlarmPopupItem, buildWorkerSafetyAlarmPopupItem, isAlarmPopupDisabledRoute, prependAlarmPopupItem, type AlarmPopupItem } from './alarm-layer-popup-model';
import { useBoardAlarmRealtime } from './BoardAlarmRealtimeProvider';
import styles from './alarm-layer-popup.module.css';

type AlarmQueueState = {
  generation: number;
  items: AlarmPopupItem[];
};

const MAX_VISIBLE_ALARM_STACK = 3;
const MIN_AUDIO_VOLUME = 0;
const MAX_AUDIO_VOLUME = 1;

type AudibleAlarmLevel = 'warning' | 'caution';

let alarmAudioContext: AudioContext | null = null;

/**
 * 숫자 값을 지정 범위 안으로 제한합니다.
 */
function clampNumber(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

/**
 * 브라우저 AudioContext를 지연 생성합니다.
 */
function getAlarmAudioContext() {
  if (typeof window === 'undefined') {
    return null;
  }

  if (alarmAudioContext) {
    return alarmAudioContext;
  }

  const AudioContextCtor = window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) {
    return null;
  }

  alarmAudioContext = new AudioContextCtor();
  return alarmAudioContext;
}

/**
 * 브라우저 자동재생 정책에 대비해 사용자 상호작용 시 오디오 컨텍스트를 활성화합니다.
 */
function unlockAlarmAudioContext() {
  const context = getAlarmAudioContext();
  if (!context || context.state !== 'suspended') {
    return;
  }

  void context.resume().catch(error => {
    console.warn('[AlarmLayerPopup] 알림음 컨텍스트 활성화 실패:', error);
  });
}

/**
 * 관리자 설정 URL이 있는 경우 파일 기반 알림음을 재생합니다.
 */
async function playConfiguredAudioUrl(url: string, volume: number) {
  const audio = new Audio(url);
  audio.volume = clampNumber(volume, MIN_AUDIO_VOLUME, MAX_AUDIO_VOLUME);
  await audio.play();
}

/**
 * Web Audio API로 짧은 비프음을 예약합니다.
 */
function scheduleToneBeep(context: AudioContext, startTime: number, frequency: number, durationSeconds: number, volume: number, type: OscillatorType) {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  const safeVolume = clampNumber(volume, MIN_AUDIO_VOLUME, MAX_AUDIO_VOLUME);

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, startTime);
  gain.gain.setValueAtTime(0.001, startTime);
  gain.gain.linearRampToValueAtTime(safeVolume, startTime + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + durationSeconds);

  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(startTime);
  oscillator.stop(startTime + durationSeconds + 0.02);
}

/**
 * 설정된 주파수로 경고/주의 기본 알림음 패턴을 재생합니다.
 */
async function playGeneratedAlarmTone(level: AudibleAlarmLevel, settings: AlarmSoundSettings) {
  const context = getAlarmAudioContext();
  if (!context) {
    return;
  }

  if (context.state === 'suspended') {
    await context.resume();
  }

  const durationSeconds = clampNumber(settings.duration_ms / 1000, 0.1, 3);
  const isWarning = level === 'warning';
  const baseFrequency = isWarning ? settings.warning_tone_frequency_hz : settings.caution_tone_frequency_hz;
  const frequencies = isWarning ? [baseFrequency, Math.round(baseFrequency * 0.82), baseFrequency] : [baseFrequency, baseFrequency];
  const gapSeconds = isWarning ? 0.08 : 0.11;
  const beepSeconds = clampNumber((durationSeconds - gapSeconds * (frequencies.length - 1)) / frequencies.length, 0.08, 0.18);
  const oscillatorType: OscillatorType = isWarning ? 'square' : 'sine';
  const startTime = context.currentTime + 0.02;

  frequencies.forEach((frequency, index) => {
    scheduleToneBeep(context, startTime + index * (beepSeconds + gapSeconds), frequency, beepSeconds, settings.volume, oscillatorType);
  });
}

/**
 * SystemConfig 설정에 맞춰 경고/주의 알림음을 재생합니다.
 */
function playAlarmSound(level: AlarmItem['level'], settings: AlarmSoundSettings) {
  if (!settings.enabled || (level !== 'warning' && level !== 'caution')) {
    return;
  }

  const soundUrl = level === 'warning' ? settings.warning_sound_url : settings.caution_sound_url;
  const playback = soundUrl ? playConfiguredAudioUrl(soundUrl, settings.volume) : playGeneratedAlarmTone(level, settings);

  void playback.catch(error => {
    console.warn('[AlarmLayerPopup] 알림음 재생 실패:', error);
  });
}

/**
 * 알람성 메시지를 전역 레이어 팝업 데이터로 변환합니다.
 */
function parseAlarmPopupItem(event: ReturnType<typeof useBoardAlarmRealtime>['event']): AlarmPopupItem | null {
  if (!event || event.transition !== 'activate') return null;
  const item = deriveAlarmItemFromMessage(event.message);
  if (!item) {
    return null;
  }

  return buildAlarmPopupItem(event.message, { ...item, id: event.occurrenceId });
}

/**
 * ALARM 수신 시 admin 제외 전역 좌측 하단 레이어 팝업을 관리합니다.
 */
export function AlarmLayerPopup() {
  const iot = useIoTFieldData();
  const pathname = usePathname();
  const disabledPath = isAlarmPopupDisabledRoute(pathname);
  const { event: realtimeAlarmEvent, fieldUid, projection: realtimeAlarmProjection } = useBoardAlarmRealtime();
  const { isLoaded: isAlarmSoundSettingsLoaded, settings: alarmSoundSettings } = useAlarmSoundSettings();
  const alarmQueueGenerationRef = useRef(0);
  const [alarmQueueState, setAlarmQueueState] = useState<AlarmQueueState>({
    generation: 0,
    items: [],
  });
  const inFlightCheckIdsRef = useRef(new Set<string>());
  const persistedAlarmUidByOccurrenceRef = useRef(new Map<string, string>());
  const alarmQueueRef = useRef<AlarmPopupItem[]>([]);
  const seenPopupOccurrencesRef = useRef(new Set<string>());
  const seenMobileSafetyEventsRef = useRef(new Set<string>());
  const alarmSoundSettingsRef = useRef(alarmSoundSettings);
  const alarmSoundSettingsLoadedRef = useRef(isAlarmSoundSettingsLoaded);
  const autoCloseTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasHandledCurrentRef = useRef(false);
  const alarmQueue = useMemo(() => (disabledPath || alarmQueueState.generation !== alarmQueueGenerationRef.current ? [] : alarmQueueState.items), [alarmQueueState, disabledPath]);
  const setAlarmQueue = useCallback((update: (previous: AlarmPopupItem[]) => AlarmPopupItem[]) => {
    const generation = alarmQueueGenerationRef.current;
    setAlarmQueueState(previous => ({
      generation,
      items: update(previous.generation === generation ? previous.items : []),
    }));
  }, []);

  const activeAlarm = useMemo(() => alarmQueue[0] ?? null, [alarmQueue]);
  const visibleAlarms = useMemo(() => alarmQueue.slice(0, MAX_VISIBLE_ALARM_STACK), [alarmQueue]);

  useEffect(() => {
    alarmQueueRef.current = alarmQueue;
  }, [alarmQueue]);

  useEffect(() => {
    alarmSoundSettingsRef.current = alarmSoundSettings;
  }, [alarmSoundSettings]);

  useEffect(() => {
    alarmSoundSettingsLoadedRef.current = isAlarmSoundSettingsLoaded;
  }, [isAlarmSoundSettingsLoaded]);

  useEffect(() => {
    if (!alarmSoundSettings.enabled || typeof window === 'undefined') {
      return;
    }

    const handleFirstInteraction = () => {
      unlockAlarmAudioContext();
    };

    window.addEventListener('pointerdown', handleFirstInteraction, { once: true, capture: true });
    window.addEventListener('keydown', handleFirstInteraction, { once: true, capture: true });

    return () => {
      window.removeEventListener('pointerdown', handleFirstInteraction, { capture: true });
      window.removeEventListener('keydown', handleFirstInteraction, { capture: true });
    };
  }, [alarmSoundSettings.enabled]);

  const clearAutoCloseTimer = useCallback(() => {
    if (!autoCloseTimerRef.current) {
      return;
    }

    clearTimeout(autoCloseTimerRef.current);
    autoCloseTimerRef.current = null;
  }, []);

  const removeActiveAlarm = useCallback(() => {
    setAlarmQueue(previous => previous.slice(1));
    hasHandledCurrentRef.current = false;
  }, [setAlarmQueue]);

  const removeAlarm = useCallback(
    (targetUid: string) => {
      setAlarmQueue(previous => previous.filter(item => item.targetUid !== targetUid));
      hasHandledCurrentRef.current = false;
    },
    [setAlarmQueue]
  );

  const confirmAlarm = useCallback(
    async (targetUid: string) => {
      const current = alarmQueue.find(item => item.targetUid === targetUid);
      if (!current || hasHandledCurrentRef.current) {
        return;
      }

      hasHandledCurrentRef.current = true;
      clearAutoCloseTimer();
      removeAlarm(current.targetUid);

      const persistedAlarmUid = current.alarmUid || persistedAlarmUidByOccurrenceRef.current.get(current.targetUid) || '';

      if (!current.fieldUid || !persistedAlarmUid) {
        hasHandledCurrentRef.current = false;
        return;
      }

      if (inFlightCheckIdsRef.current.has(persistedAlarmUid)) {
        hasHandledCurrentRef.current = false;
        return;
      }

      inFlightCheckIdsRef.current.add(persistedAlarmUid);
      try {
        const result = await updateAlarmCheck({
          provider: iot.provider,
          field_uid: current.fieldUid,
          target_uid: persistedAlarmUid,
          is_check: true,
        });

        if (!result.ok) {
          console.error('[AlarmLayerPopup] 알람 확인 API 실패:', result.error);
        }
      } catch (error) {
        console.error('[AlarmLayerPopup] 알람 확인 API 호출 예외:', error);
      } finally {
        inFlightCheckIdsRef.current.delete(persistedAlarmUid);
        hasHandledCurrentRef.current = false;
      }
    },
    [alarmQueue, clearAutoCloseTimer, removeAlarm, iot.provider]
  );

  useEffect(() => {
    if (!disabledPath) {
      return;
    }

    clearAutoCloseTimer();
    alarmQueueGenerationRef.current += 1;
    alarmQueueRef.current = [];
    hasHandledCurrentRef.current = false;
  }, [disabledPath, clearAutoCloseTimer]);

  useEffect(() => {
    clearAutoCloseTimer();
    alarmQueueGenerationRef.current += 1;
    alarmQueueRef.current = [];
    seenPopupOccurrencesRef.current.clear();
    seenMobileSafetyEventsRef.current.clear();
    persistedAlarmUidByOccurrenceRef.current.clear();
    hasHandledCurrentRef.current = false;
  }, [clearAutoCloseTimer, fieldUid]);

  useEffect(() => {
    const persistedAlarmUids = new Map<string, string>();
    realtimeAlarmProjection.forEach(event => {
      if (event.alarmUid) persistedAlarmUids.set(event.occurrenceId, event.alarmUid);
    });
    persistedAlarmUidByOccurrenceRef.current = persistedAlarmUids;
  }, [realtimeAlarmProjection]);

  useEffect(() => {
    if (!activeAlarm) {
      clearAutoCloseTimer();
      return;
    }

    clearAutoCloseTimer();
    autoCloseTimerRef.current = setTimeout(() => {
      removeActiveAlarm();
      hasHandledCurrentRef.current = false;
    }, 10_000);

    return clearAutoCloseTimer;
  }, [activeAlarm, clearAutoCloseTimer, removeActiveAlarm]);

  useEffect(() => {
    if (!realtimeAlarmEvent || disabledPath) return;
    if (realtimeAlarmEvent.transition === 'clear') {
      const clearedAlarmUid = realtimeAlarmEvent.clearedAlarmUid || realtimeAlarmEvent.alarmUid;
      setAlarmQueue(previous => previous.filter(item => item.targetUid !== realtimeAlarmEvent.occurrenceId && (!clearedAlarmUid || item.alarmUid !== clearedAlarmUid)));
    }

    // 같은 렌더에서 경고와 진입 정보가 함께 수신돼도 마지막 이벤트만 표시하지 않습니다.
    const activeOccurrences = new Set(realtimeAlarmProjection.map(event => event.occurrenceId));
    seenPopupOccurrencesRef.current = new Set([...seenPopupOccurrencesRef.current].filter(id => activeOccurrences.has(id)));
    const pendingEvents = realtimeAlarmProjection.filter(event => !seenPopupOccurrencesRef.current.has(event.occurrenceId));
    for (const event of pendingEvents.sort((left, right) => Number(left.severity === 'danger') - Number(right.severity === 'danger'))) {
      seenPopupOccurrencesRef.current.add(event.occurrenceId);
      const parsed = parseAlarmPopupItem(event);
      if (!parsed || alarmQueueRef.current.some(item => item.targetUid === parsed.targetUid)) continue;

      debugRealtimeAlarmStage('popup_enqueued', {
        correlation_id: event.correlationId || null,
        event_at_ms: event.eventAtMs,
        field_uid: event.fieldUid,
        occurrence_id: event.occurrenceId,
        received_at_ms: event.receivedAtMs,
      });

      if (alarmSoundSettingsLoadedRef.current) playAlarmSound(parsed.level, alarmSoundSettingsRef.current);

      setAlarmQueue(previous => {
        const nextQueue = prependAlarmPopupItem(previous, parsed, MAX_VISIBLE_ALARM_STACK);
        alarmQueueRef.current = nextQueue;
        return nextQueue;
      });
    }
  }, [disabledPath, realtimeAlarmEvent, realtimeAlarmProjection, setAlarmQueue]);

  useEffect(() => {
    if (disabledPath) return;
    for (const event of iot.mobileSafetyEvents) {
      if (event.field_uid !== fieldUid || seenMobileSafetyEventsRef.current.has(event.event_id)) continue;
      seenMobileSafetyEventsRef.current.add(event.event_id);
      if (seenMobileSafetyEventsRef.current.size > 512) seenMobileSafetyEventsRef.current.delete(seenMobileSafetyEventsRef.current.values().next().value!);
      const parsed = buildWorkerSafetyAlarmPopupItem(event);
      if (!parsed) continue;

      if (alarmSoundSettingsLoadedRef.current) playAlarmSound(parsed.level, alarmSoundSettingsRef.current);

      setAlarmQueue(previous => {
        const nextQueue = prependAlarmPopupItem(previous, parsed, MAX_VISIBLE_ALARM_STACK);
        alarmQueueRef.current = nextQueue;
        return nextQueue;
      });
    }
  }, [disabledPath, fieldUid, iot.mobileSafetyEvents, setAlarmQueue]);

  if (disabledPath || !activeAlarm) {
    return null;
  }

  return (
    <div className={styles.viewportBottomLeft} role="presentation">
      {visibleAlarms.map((alarm, index) => (
        <AlarmNotificationModal
          key={alarm.targetUid}
          deviceId={null}
          headline={alarm.bodyMessage}
          inline
          message={alarm.description}
          severityLabel={alarm.severityText}
          style={{
            zIndex: zIndex.realtimeNotification - index,
          }}
          timestamp={alarm.occurredAt}
          open
          onClose={() => confirmAlarm(alarm.targetUid)}
        />
      ))}
    </div>
  );
}
